<template>
  <div :class="$style.month">
    <div :class="$style.periodHeader">
      <SCADropdown v-if="yearSelectable" v-model:is-open="yearOpen" placement="bottom" :class="$style.yearDropdown">
        <template #trigger="{ target }">
          <button type="button" :class="$style.periodLabel" :popovertarget="target" :test-id="`${testId}-period`">
            {{ monthLabel }}
            <SCASvgIcon
              :class="[$style.caret, { [$style.caretOpen]: yearOpen }]"
              icon="phosphor/caret-down"
              size="12px"
            />
          </button>
        </template>
        <div ref="yearListEl" :class="$style.yearList">
          <button
            v-for="year in years"
            :key="year"
            :ref="(el) => setYearRef(el, year)"
            type="button"
            :class="[$style.yearItem, { [$style.yearActive]: year === currentYear }]"
            :test-id="`${testId}-year-${year}`"
            @click="onSelectYear(year)"
          >
            {{ year }}
          </button>
        </div>
      </SCADropdown>
      <span v-else :class="$style.periodLabelStatic">{{ monthLabel }}</span>
    </div>

    <div :class="$style.grid">
      <div :class="$style.weekdayRow">
        <div v-if="showWeekNumbers" :class="[$style.weekdayCell, $style.weekColumn]">
          {{ weekLabel }}
        </div>
        <div v-for="(name, i) in weekdays" :key="i" :class="$style.weekdayCell">
          {{ name }}
        </div>
      </div>

      <div v-for="(row, rowIndex) in rows" :key="rowIndex" :class="$style.weekRow">
        <div v-if="showWeekNumbers" :class="$style.weekNumber">
          {{ row.weekNumber }}
        </div>
        <div :class="$style.daysWrap">
          <span :class="$style.rangeBar" :style="rowBarStyle(row)" />
          <CalendarDayCell
            v-for="(cell, colIndex) in row.days"
            :key="cell.date.getTime()"
            :cell="cell"
            :state="getDayState(cell.date)"
            :is-row-start="colIndex === 0"
            :is-row-end="colIndex === row.days.length - 1"
            :readonly="readonly"
            :test-id="`${testId}-day-${cell.date.getFullYear()}-${cell.date.getMonth() + 1}-${cell.day}`"
            @select="$emit('select', $event)"
            @hover="$emit('hover', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Day } from "date-fns";
import { computed, nextTick, ref, watch } from "vue";

import SCADropdown from "../Dropdown/SCADropdown.vue";
import SCASvgIcon from "../SCASvgIcon.vue";
import CalendarDayCell from "./CalendarDayCell.vue";
import { buildMonthRows } from "./calendarUtils";
import type { DaySelectionState, WeekRow } from "./datetimePickerTypes";

const props = withDefaults(
  defineProps<{
    month: Date;
    weekStartsOn: Day;
    showWeekNumbers: boolean;
    weekdays: string[];
    weekLabel: string;
    monthLabel: string;
    years: number[];
    yearSelectable: boolean;
    isDisabled: (date: Date) => boolean;
    getDayState: (date: Date) => DaySelectionState;
    readonly?: boolean;
    testId?: string;
  }>(),
  {
    readonly: false,
    testId: "sca-calendar-month",
  },
);

const emit = defineEmits<{
  select: [date: Date];
  hover: [date: Date];
  selectYear: [year: number];
}>();

const yearOpen = ref(false);
const yearListEl = ref<HTMLElement | null>(null);
const activeYearEl = ref<HTMLElement | null>(null);

function setYearRef(el: unknown, year: number) {
  if (year === currentYear.value) {
    activeYearEl.value = (el as { $el?: HTMLElement })?.$el ?? (el as HTMLElement | null);
  }
}

const rows = computed(() => buildMonthRows(props.month, props.weekStartsOn, props.isDisabled));
const currentYear = computed(() => props.month.getFullYear());

watch(yearOpen, async (open) => {
  if (!open) return;
  await nextTick();
  const list = yearListEl.value;
  const active = activeYearEl.value;
  if (!list || !active) return;
  list.scrollTop = active.offsetTop - list.clientHeight / 2 + active.clientHeight / 2;
});

function onSelectYear(year: number) {
  yearOpen.value = false;
  emit("selectYear", year);
}

function cellFill(s: DaySelectionState): "full" | "left" | "right" | "none" {
  if (s.rangeStart && s.rangeEnd) return "none"; // single selected day
  if (s.previewStart || s.previewEnd) return "full"; // hovered endpoint: highlight the whole cell
  if (s.rangeStart) return "right"; // bar extends toward the range end
  if (s.rangeEnd) return "left"; // bar extends toward the range start
  if (s.inRange || s.preview) return "full";
  return "none";
}

function rowBarStyle(row: WeekRow): { left: string; width: string } {
  const cols = row.days.length;
  let start: number | null = null;
  let end: number | null = null;
  let direction: DaySelectionState["previewDirection"];

  for (const [i, cell] of row.days.entries()) {
    const state = props.getDayState(cell.date);
    direction ??= state.previewDirection;
    const fill = cellFill(state);
    if (fill === "none") continue;
    const from = fill === "right" ? i + 0.5 : i;
    const to = fill === "left" ? i + 0.5 : i + 1;
    if (start === null || from < start) start = from;
    if (end === null || to > end) end = to;
  }

  if (start === null || end === null) {
    return { left: direction === "backward" ? "100%" : "0%", width: "0%" };
  }
  return { left: `${(start / cols) * 100}%`, width: `${((end - start) / cols) * 100}%` };
}
</script>

<style module lang="scss">
@use "../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../../styles/icon.scss" as IconMixins;

.month {
  display: flex;
  flex-direction: column;
}

.periodHeader {
  position: sticky;
  // Sits directly below the sticky toolbar (44px tall) in SCACalendarPanel.
  top: 44px;
  // Above the day-cell circles (z-index 1) so they scroll underneath, but below
  // the toolbar (z-index 3) which pins above this header.
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: var(--space-component-padding-xSmall) var(--space-component-padding-medium);
  border-bottom: 1px solid var(--color-border-default, #eaedf0);
  background-color: var(--color-interaction-background-modeless, #fff);
}

.yearDropdown {
  width: auto;
}

.periodLabel,
.periodLabelStatic {
  @include FigmaTypoMixins.typo-body-strong-medium;
  height: 28px;
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  color: var(--color-content-brand-primary, #113997);
  letter-spacing: 0.24px;
  white-space: nowrap;
}

.periodLabel {
  cursor: pointer;
  margin-left: var(--space-component-padding-large);
  gap: var(--space-component-padding-xSmall);
}

.periodLabel .caret {
  flex-shrink: 0;
  pointer-events: none;
  opacity: 0;
  @include IconMixins.icon-color(var(--color-content-brand-primary, #113997));
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.periodLabel:hover .caret,
.periodLabel .caret.caretOpen {
  opacity: 1;
}

.caretOpen {
  transform: rotate(180deg);
}

.yearList {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-2xSmall, 2px);
  max-height: 240px;
  overflow-y: auto;
  // Keep wheel/touch scrolling inside the list — don't chain to the page when hitting an edge.
  overscroll-behavior: contain;
  min-width: 96px;
}

.yearItem {
  @include FigmaTypoMixins.typo-body-medium;
  padding: var(--space-component-padding-xSmall) var(--space-component-padding-small);
  border: none;
  background: transparent;
  border-radius: var(--border-radius-medium, 6px);
  color: var(--color-content-primary, #272e35);
  text-align: center;
  cursor: pointer;

  &:hover {
    background-color: var(--color-interaction-ghost-hover, rgba(16, 40, 72, 0.06));
  }
}

.yearActive {
  background-color: var(--color-background-informative-onSubtle);

  &:hover {
    background-color: var(--color-background-informative-onSubtle);
  }
}

.grid {
  display: flex;
  flex-direction: column;
  padding: 10px;
}

.weekdayRow,
.weekRow {
  display: flex;
  align-items: center;
}

.daysWrap {
  position: relative;
  display: flex;
  align-items: center;
}

.rangeBar {
  position: absolute;
  top: 4px;
  bottom: 4px;
  background-color: var(--color-background-informative-onSubtle, #f5f8ff);
  transition:
    left 0.16s ease-out,
    width 0.16s ease-out;
  pointer-events: none;
}

.weekdayCell {
  @include FigmaTypoMixins.typo-body-medium;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--sca-dp-cell-size, 40px);
  height: 28px;
  color: var(--color-content-disabled, #8896a6);
  letter-spacing: 0.24px;
}

.weekColumn {
  background-color: var(--color-interaction-neutral-subtle-normal, #f0f3f5);
  border-top-left-radius: var(--border-radius-medium, 4px);
  border-top-right-radius: var(--border-radius-medium, 4px);
}

.weekNumber {
  @include FigmaTypoMixins.typo-body-medium;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--sca-dp-cell-size, 40px);
  height: var(--sca-dp-cell-size, 40px);
  background-color: var(--color-interaction-neutral-subtle-normal, #f0f3f5);
  color: var(--color-content-tertiary, #6b7682);
  letter-spacing: 0.24px;
}

.weekRow:last-child .weekNumber {
  border-bottom-left-radius: var(--border-radius-small, 4px);
  border-bottom-right-radius: var(--border-radius-small, 4px);
}

@media (max-width: 768px) {
  .month,
  .grid {
    width: 100%;
  }
  .weekdayRow,
  .weekRow {
    width: 100%;
  }
  .weekdayCell,
  .weekNumber {
    flex: 1 1 0;
    width: auto;
    min-width: 32px;
  }
  /* Match the 1:7 split of week-number column vs day cells so bar percentages stay aligned. */
  .daysWrap {
    flex: 7 1 0;
    min-width: 0;
  }
}
</style>
